import React, { ReactNode } from 'react';
import styled, { css } from 'styled-components';
import { lineHeight, theme, toRem } from 'design';
import { Align, Appearance, FontWeight } from 'design/types';

interface IMessageProps {
  appearance?: Appearance;
  alignMessage?: Align;
  className?: string;
  dataCy?: string;
  fitContent?: boolean;
  fontWeight?: FontWeight;
  title?: string;
  message: string | ReactNode;
}

// Appearances with a tinted background; any other is neutral.
const BACKGROUND_COLOR: Partial<Record<Appearance, string>> = {
  [Appearance.SUCCESS]: theme.color.success[25],
  [Appearance.WARNING]: theme.color.warning[25],
  [Appearance.DANGER]: theme.color.danger[25],
  [Appearance.INFO]: theme.color.info[25],
};

const Layout = styled.div<Partial<IMessageProps>>`
  display: flex;
  flex-flow: column nowrap;
  font-family: ${theme.font.family.euclid};
  font-size: ${toRem(theme.font.size.md)};
  background-color: ${({ appearance }) => BACKGROUND_COLOR[appearance] ?? theme.color.neutral[100]};
  border-radius: ${toRem(8)};
  color: ${theme.color.neutral[900]};
  line-height: ${lineHeight(theme.font.size.md)};
  width: 100%;
  padding: ${toRem(theme.space.xl)};
  word-break: break-word;

  ${({ fitContent }) =>
    fitContent &&
    css`
      width: fit-content;
      height: fit-content;
    `};
`;

const Title = styled.h4<{ appearance?: Appearance; align?: Align }>`
  color: ${({ appearance }) => (appearance === Appearance.DANGER ? theme.color.danger[50] : 'inherit')};
  font-weight: ${theme.font.weight.semiBold};

  ${({ align }) =>
    align &&
    css`
      text-align: ${align};
    `};
`;

const toFlexAlign = (align?: Align) => {
  if (align === Align.CENTER) return 'center';
  if (align === Align.RIGHT || align === Align.END) return 'flex-end';
  return 'flex-start';
};

const Content = styled.p<{ fontWeight?: FontWeight; align?: Align }>`
  display: flex;
  flex-flow: column nowrap;

  ${({ align }) =>
    align &&
    css`
      text-align: ${align};
      align-items: ${toFlexAlign(align)};
      justify-content: ${toFlexAlign(align)};
    `};

  ${({ fontWeight }) =>
    fontWeight &&
    css`
      font-weight: ${fontWeight};
    `};
`;

export const Message = ({ appearance, className = '', dataCy = 'message', fontWeight, fitContent = false, title, message: content, alignMessage }: IMessageProps) => {
  const isStringContent = typeof content === 'string';
  const contentAlign = alignMessage ?? (title ? undefined : Align.CENTER);
  return (
    <Layout data-cy={dataCy} className={className} appearance={appearance} fitContent={fitContent}>
      {title && (
        <Title data-cy={`${dataCy}-title`} appearance={appearance} align={alignMessage}>
          {title}
        </Title>
      )}
      <Content data-cy={`${dataCy}-content`} fontWeight={fontWeight} as={isStringContent ? 'p' : 'div'} align={contentAlign}>
        {content}
      </Content>
    </Layout>
  );
};
